import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useCallback } from 'react';
import {
  ActionEvents,
  taskTypeToTrackingTaskType,
} from 'shared/services/tracking';
import { NewGoalWithID, UpdateGoal } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import { UpdateLifeArea } from 'shared/types/life-area';
import { MinimalRelationship } from 'shared/types/relationship';
import { NewTagWithID } from 'shared/types/tag';
import { CreateTask, NewSubTask, NewTask } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { createPrefixId } from 'shared/utils/prefix-id';

import { useCreateGoalsWithIdsMutation } from './queries/use-create-goals-with-ids-mutation';
import { useCreateRelationshipsMutation } from './queries/use-create-relationships-mutation';
import { useCreateTagsWithIdsMutation } from './queries/use-create-tags-with-ids-mutation';
import { useCreateTaskWithSubTasksMutation } from './queries/use-create-task-with-sub-tasks-mutation';
import { useUpdateGoalMutation } from './queries/use-update-goal-mutation';
import { useUpdateLifeAreaMutation } from './queries/use-update-life-area-mutation';
import { useUpdateRelationshipsReferencesMutation } from './queries/use-update-relationships-references-mutation';
import { useRelationships } from './use-relationships';
import { useTrackEvents } from './use-track-events';

export type Options = {
  task: CreateTask;
  subTasks?: NewSubTask[];
  goalsToCreate?: NewGoalWithID[];
  tagsToCreate?: NewTagWithID[];
};

export const useCreateTasksWithEntities = () => {
  const track = useTrackEvents();

  const { mutateAsync: createTask } = useCreateTaskWithSubTasksMutation();
  const { mutateAsync: createGoals } = useCreateGoalsWithIdsMutation();
  const { mutateAsync: updateGoal } = useUpdateGoalMutation();
  const { mutateAsync: updateLifeArea } = useUpdateLifeAreaMutation();
  const { mutateAsync: createTags } = useCreateTagsWithIdsMutation();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const relationships = useRelationships();
  const { mutateAsync: createRelationships } = useCreateRelationshipsMutation();
  const { mutateAsync: updateRelationships } =
    useUpdateRelationshipsReferencesMutation();

  return useCallback(
    async ({ task, subTasks, tagsToCreate, goalsToCreate }: Options) => {
      const { tagIds, ..._task } = task;
      // create goal if needed
      const goalToCreate = goalsToCreate?.find(({ id }) => id === _task.goalId);
      if (goalToCreate) {
        const { goals: createdGoals } = await createGoals([
          { ...goalToCreate, taskCount: 1 },
        ]);
        _task.goalId = createdGoals[0].id;
      }

      // create tags if needed
      const _tagsToCreate = tagsToCreate?.filter(({ id }) =>
        tagIds?.includes(id),
      );

      const { oldIdToNewIdMap: tagIdMap } = _tagsToCreate
        ? await createTags(_tagsToCreate)
        : { oldIdToNewIdMap: {} as Record<ID, ID> };

      // create the actual task (with subtasks)
      const createdTask = await createTask({
        task: {
          ..._task,
          startDate: new Date(),
          createdAt: new Date(),
          endStrategy: task.endStrategy
            ? {
                deadline: task.endStrategy?.deadline ?? null,
                completionCount: task.endStrategy?.completionCount ?? null,
              }
            : null,
        } as NewTask,
        subTasks: subTasks ?? [],
      });

      // update goal (if not created)
      if (!goalToCreate && createdTask.goalId) {
        const goalToUpdate = goals.find(({ id }) => id === createdTask.goalId);
        const goalBase: UpdateGoal = {
          id: createdTask.goalId,
        };

        if (_task.sectionId && goalToUpdate) {
          goalBase.sections = goalToUpdate.sections?.map((section) => {
            if (section.id === _task.sectionId) {
              return {
                ...section,
                tasks: Array.from(new Set([...section.tasks, createdTask.id])),
              };
            }
            return section;
          });
        }

        if (createdTask.action?.type !== TaskActionType.GoalTimeslot) {
          goalBase.taskCount = 1;
        }

        await updateGoal({ goal: goalBase });
      }

      // update life area
      if (createdTask.lifeAreaId && _task.sectionId) {
        const lifeAreaToUpdate = lifeAreas.find(
          ({ id }) => id === createdTask.lifeAreaId,
        );
        const lifeAreaBase: UpdateLifeArea = {
          id: createdTask.lifeAreaId,
        };

        if (lifeAreaToUpdate) {
          lifeAreaBase.sections = lifeAreaToUpdate.sections?.map((section) => {
            if (section.id === _task.sectionId) {
              return {
                ...section,
                tasks: Array.from(new Set([...section.tasks, createdTask.id])),
              };
            }
            return section;
          });

          if (lifeAreaBase.sections) {
            await updateLifeArea(lifeAreaBase);
          }
        }
      }

      // update task relationships
      if (tagIds?.length) {
        const relationshipsToUpdate: MinimalRelationship[] = [];
        const relationshipsToCreate: MinimalRelationship[] = [];

        tagIds.forEach((tagId) => {
          // prep relationship data
          const _tagId = tagIdMap[tagId] ?? tagId;
          const prefixedId = createPrefixId(_tagId, IDPrefixes.Tag);
          let relationship: MinimalRelationship | undefined =
            relationships.find(({ id }) => id === prefixedId);

          const list = relationship
            ? relationshipsToUpdate
            : relationshipsToCreate;

          if (!relationship) {
            relationship = {
              id: prefixedId,
              references: [],
            };
          }

          relationship.references = Array.from(
            new Set([
              ...relationship.references,
              createPrefixId(createdTask.id, IDPrefixes.Task),
            ]),
          );
          list.push(relationship);
        });

        // create and update relationships
        if (relationshipsToCreate.length) {
          await createRelationships(relationshipsToCreate);
        }
        if (relationshipsToUpdate.length) {
          await updateRelationships(relationshipsToUpdate);
        }
      }

      // track the creation of the task
      track(ActionEvents.TaskCreate, {
        type: taskTypeToTrackingTaskType[createdTask.type],
        deadline_added: !!createdTask.endStrategy?.deadline,
        linked_goal: !!createdTask.goalId,
      });

      return createdTask;
    },
    [
      createGoals,
      createRelationships,
      createTags,
      createTask,
      goals,
      lifeAreas,
      relationships,
      track,
      updateGoal,
      updateLifeArea,
      updateRelationships,
    ],
  );
};
